import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/api/endpoints/auth';
import { meApi } from '@/api/endpoints/me';
import { authCookies } from '@/api/cookies';
import { normalizeErrors } from '@/api/errors';
import { useAppDispatch } from '@/app/hooks';
import {
  setAuthenticated,
  setGuest,
  setMe,
  setPending2FA,
} from './authSlice';
import type {
  AuthTokenRequest,
  Check2FARequest,
  LoginRequest,
  MeResponse,
  RegisterRequest,
  RequestResetPasswordRequest,
  ResetPasswordRequest,
  VerifyRequest,
} from '@/types/api';

// React-query key для GET /me. Используется и в queryClient.invalidateQueries
// после успешного login/check2fa чтобы сразу подтянуть фактический /me.
export const ME_QUERY_KEY = ['me'] as const;

// useMe — фоновый poll профиля пользователя. Включается только если есть HMAC
// cookies (auth_key + auth_2af). Иначе сервер вернёт 401, что в request-интерсепторе
// клиента уже превращается в "выкидывание" guest'а.
//
// Side-effect: на успешный fetch диспатчит setAuthenticated(data) в RTK store.
// Это позволяет RequireAuth/RedirectIfAuthed и другим guards читать status из
// auth-slice (а не дублировать query). На очистку cookies guest-статус ставит
// detectUnauthorized-handler в client.ts → setGuest dispatch.
export function useMe() {
  const dispatch = useAppDispatch();
  const query = useQuery<MeResponse>({
    queryKey: ME_QUERY_KEY,
    queryFn: () => meApi.get(),
    enabled: authCookies.hasFullAuth(),
    // /me редко меняется в рамках сессии — кэшируем дольше дефолта.
    staleTime: 60_000,
  });

  useEffect(() => {
    if (query.data) {
      dispatch(setAuthenticated(query.data));
    }
  }, [dispatch, query.data]);

  return query;
}

// useLogin — POST /user/login. На успех:
// 1) ставит HMAC cookies (auth_key, auth_user_id, auth_2af),
// 2) переводит RTK store в authenticated,
// 3) инвалидирует useMe чтобы подтянуть профиль.
//
// На 2FA-флоу — переводит в pending-2fa, cookies оставляет частичными
// (только auth_user_id + auth_2af=false; auth_key придёт после check2fa).
//
// На validation/not-activated — возвращает discriminated union через
// onSuccess-выход, callback в UI разруливает.
export type LoginOutcome =
  | { kind: 'authenticated' }
  | { kind: 'need-2fa' }
  | { kind: 'not-activated' }
  | { kind: 'validation'; errors: Record<string, string[]> };

export function useLogin() {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  return useMutation<LoginOutcome, Error, LoginRequest>({
    mutationFn: async (req) => {
      const res = await authApi.login(req);

      if ('error_validate' in res) {
        return {
          kind: 'validation',
          errors: normalizeErrors(res.error_validate),
        };
      }

      // Yii возвращает TINYINT(1) как число (0/1) — нужно truthy/falsy.
      const needs2fa = Boolean(res.is_2fa_auth) && res.auth === false;

      if (needs2fa) {
        authCookies.setUserId(res.auth_user_id);
        authCookies.set2fa(false);
        dispatch(
          setPending2FA({
            temporaryToken: res.temporaryToken,
            auth_user_id: res.auth_user_id,
            rememberMe: res.rememberMe,
          }),
        );
        return { kind: 'need-2fa' };
      }

      if (typeof res.auth === 'string') {
        if (!res.is_activated) {
          authCookies.setUserId(res.auth_user_id);
          return { kind: 'not-activated' };
        }
        authCookies.setAuthKey(res.auth);
        authCookies.setUserId(res.auth_user_id);
        authCookies.set2fa(true);
        // Синхронно переводим в authenticated, чтобы редирект в UI увидел
        // правильный state и не отбрасывал обратно на /login.
        dispatch(setAuthenticated(null));
        void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
        return { kind: 'authenticated' };
      }

      return { kind: 'validation', errors: {} };
    },
  });
}

// useCheck2fa — POST /user/check2fa. Дёргается после useLogin → 'need-2fa'.
// На успех ставит auth_key cookie + переводит store в authenticated +
// инвалидирует /me.
export type Check2FAOutcome =
  | { kind: 'authenticated' }
  | { kind: 'invalid' };

export function useCheck2fa() {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  return useMutation<Check2FAOutcome, Error, Check2FARequest>({
    mutationFn: async (req) => {
      const res = await authApi.check2fa(req);
      if (res.auth === false) {
        return { kind: 'invalid' };
      }
      authCookies.setAuthKey(res.auth);
      authCookies.set2fa(true);
      dispatch(setAuthenticated(null));
      void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
      return { kind: 'authenticated' };
    },
  });
}

// useRegister — POST /user/register. На успех возвращает id созданного юзера;
// его дальше использует страница активации.
export type RegisterOutcome =
  | { kind: 'ok'; userId: number }
  | { kind: 'error' }
  | { kind: 'validation'; errors: Record<string, string[]> };

export function useRegister() {
  return useMutation<RegisterOutcome, Error, RegisterRequest>({
    mutationFn: async (req) => {
      const res = await authApi.register(req);
      if ('error_validate' in res) {
        return {
          kind: 'validation',
          errors: normalizeErrors(res.error_validate),
        };
      }
      if (res.status === 1) {
        return { kind: 'ok', userId: res.id };
      }
      return { kind: 'error' };
    },
  });
}

// useRequestPasswordReset — POST /user/requestResetPassword.
export type ResetOutcome =
  | { kind: 'ok' }
  | { kind: 'error' }
  | { kind: 'validation'; errors: Record<string, string[]> };

export function useRequestPasswordReset() {
  return useMutation<ResetOutcome, Error, RequestResetPasswordRequest>({
    mutationFn: async (req) => {
      const res = await authApi.requestResetPassword(req);
      if ('validation_error' in res) {
        return {
          kind: 'validation',
          errors: normalizeErrors(res.validation_error),
        };
      }
      return res.status === 1 ? { kind: 'ok' } : { kind: 'error' };
    },
  });
}

// useResetPassword — POST /user/resetPassword/{token}/{hash}. Принимает token и hash
// из URL-параметров письма + новый пароль с подтверждением. Аутентификации не
// требует (cookies могут быть пусты).
export type ResetPasswordOutcome =
  | { kind: 'ok' }
  | { kind: 'error' }
  | { kind: 'validation'; errors: Record<string, string[]> };

export function useResetPassword() {
  return useMutation<
    ResetPasswordOutcome,
    Error,
    { token: string; hash: string; data: ResetPasswordRequest }
  >({
    mutationFn: async ({ token, hash, data }) => {
      const res = await authApi.resetPassword(token, hash, data);
      if ('error_validate' in res) {
        return { kind: 'validation', errors: normalizeErrors(res.error_validate) };
      }
      return res.status === 1 ? { kind: 'ok' } : { kind: 'error' };
    },
  });
}

// useVerifyEmail — POST /user/verify. Подтверждение email после регистрации
// (token + hash из ссылки в письме). На success бек отдаёт auth_key — мы
// сохраняем cookies + переводим store в authenticated. Аутентификация не
// требуется на момент запроса.
export type VerifyOutcome =
  | { kind: 'ok' }
  | { kind: 'error'; message?: string };

export function useVerifyEmail() {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  return useMutation<VerifyOutcome, Error, VerifyRequest>({
    mutationFn: async (req) => {
      try {
        const res = await authApi.verify(req);
        if ('auth_key' in res && typeof res.auth_key === 'string') {
          authCookies.setAuthKey(res.auth_key);
          if (typeof res.auth_user_id === 'number') {
            authCookies.setUserId(res.auth_user_id);
          }
          authCookies.set2fa(true);
          dispatch(setAuthenticated(null));
          void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
          return { kind: 'ok' };
        }
        if ('error_validate' in res) {
          const errs = normalizeErrors(res.error_validate);
          const firstMsg = Object.values(errs)[0]?.[0];
          return { kind: 'error', message: firstMsg };
        }
        return { kind: 'error' };
      } catch {
        return { kind: 'error' };
      }
    },
  });
}

// useAuthToken — POST /user/authToken. Авторизация по временному токену
// (impersonation: менеджер из админки открывает /partners/token?id=&token=).
// 1:1 с легаси TokenCtrl + Authorization.token(): на success бек отдаёт auth_key
// (поле `auth`), мы ставим cookies + переводим store в authenticated. 2FA при
// impersonation минуется — ставим auth_2af=true (иначе hasFullAuth/RequireAuth
// не пустят), как в authenticated-ветке useLogin.
export type AuthTokenOutcome =
  | { kind: 'ok' }
  | { kind: 'not-activated' }
  | { kind: 'error'; message?: string };

export function useAuthToken() {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  return useMutation<AuthTokenOutcome, Error, AuthTokenRequest>({
    mutationFn: async (req) => {
      try {
        const res = await authApi.authToken(req);
        if ('error_validate' in res) {
          const errs = normalizeErrors(res.error_validate);
          return { kind: 'error', message: Object.values(errs)[0]?.[0] };
        }
        // Легаси проверял is_activated до авторизации — повторяем.
        if (!res.is_activated) {
          return { kind: 'not-activated' };
        }
        authCookies.setAuthKey(res.auth);
        authCookies.setUserId(res.auth_user_id);
        authCookies.set2fa(true);
        dispatch(setAuthenticated(null));
        void queryClient.invalidateQueries({ queryKey: ME_QUERY_KEY });
        return { kind: 'ok' };
      } catch {
        return { kind: 'error' };
      }
    },
  });
}

// useLogout — синхронная операция: чистит cookies + сбрасывает RTK store.
// Возвращает functional handler (а не mutation) — серверного запроса нет,
// сервер сам узнает по отсутствию auth_key в подписи следующего запроса.
export function useLogout() {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();

  return () => {
    authCookies.clear();
    dispatch(setGuest());
    queryClient.removeQueries({ queryKey: ME_QUERY_KEY });
  };
}

// Re-export для удобства потребителей.
export { setMe };
